import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

// --- Title "click to reveal, click again to navigate" behaviour ---
const REVEAL_ARM_WINDOW = 1400; // ms the title stays "armed" waiting for the confirming click
const REVEAL_CHAR_STAGGER = 26; // ms delay between each revealed character (must match the CSS)
const REVEAL_RESET_BUFFER = 420; // ms after the last character animates before we allow a replay

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /** @type {Map<HTMLAnchorElement, ReturnType<typeof setTimeout>>} */
  #armedLinks = new Map();

  /** @type {Map<HTMLElement, ReturnType<typeof setTimeout>>} */
  #revealResetTimeouts = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.#setupTitleReveal();

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    this.#armedLinks.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#armedLinks.clear();

    this.#revealResetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#revealResetTimeouts.clear();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Prepares every top level menu title for the letter-by-letter reveal
   * animation and wires up the "first click reveals, second click navigates"
   * behaviour. Runs once on connect; the underlying markup from
   * snippets/header-menu.liquid is not modified, everything happens here.
   */
  #setupTitleReveal = () => {
    const links = this.querySelectorAll('[ref="menuitem"]');

    links.forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const title = link.querySelector('.menu-list__link-title');
      if (!(title instanceof HTMLElement)) return;

      this.#splitTitleIntoChars(title);

      link.addEventListener('click', (event) => this.#handleTitleClick(event, link, title), {
        signal: this.#abortController.signal,
      });
    });
  };

  /**
   * Wraps every character of a title in its own span so it can be revealed
   * individually with a staggered animation. Safe to call more than once;
   * only runs the first time for a given title.
   * @param {HTMLElement} title
   */
  #splitTitleIntoChars = (title) => {
    if (title.dataset.split === 'true') return;

    const text = title.textContent?.trim() ?? '';
    if (!text) return;

    // Keep the title accessible to screen readers as a single label, the
    // per-character spans below are purely decorative.
    title.setAttribute('aria-label', text);
    title.textContent = '';

    const charWrapper = document.createElement('span');
    charWrapper.className = 'menu-list__link-title-chars';
    charWrapper.setAttribute('aria-hidden', 'true');

    [...text].forEach((char, index) => {
      const charSpan = document.createElement('span');
      charSpan.className = 'menu-list__link-title-char';
      charSpan.style.setProperty('--char-index', String(index));
      charSpan.textContent = char === ' ' ? '\u00A0' : char;
      charWrapper.appendChild(charSpan);
    });

    title.appendChild(charWrapper);
    title.dataset.split = 'true';
  };

  /**
   * First click on a menu title plays the reveal animation and blocks
   * navigation. A confirming second click within REVEAL_ARM_WINDOW is left
   * alone so the browser follows the link as normal.
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   * @param {HTMLElement} title
   */
  #handleTitleClick = (event, link, title) => {
    if (link.dataset.armed === 'true') {
      this.#disarm(link);
      return;
    }

    // Only one title should be "waiting" for its confirming click at a time.
    [...this.#armedLinks.keys()].forEach((otherLink) => {
      if (otherLink !== link) this.#disarm(otherLink);
    });

    event.preventDefault();

    this.#playReveal(title);

    link.dataset.armed = 'true';
    const timeoutId = setTimeout(() => this.#disarm(link), REVEAL_ARM_WINDOW);
    this.#armedLinks.set(link, timeoutId);
  };

  /**
   * @param {HTMLAnchorElement} link
   */
  #disarm = (link) => {
    delete link.dataset.armed;

    const timeoutId = this.#armedLinks.get(link);
    if (timeoutId) clearTimeout(timeoutId);
    this.#armedLinks.delete(link);
  };

  /**
   * (Re)starts the letter by letter reveal animation on a title.
   * @param {HTMLElement} title
   */
  #playReveal = (title) => {
    const existingTimeout = this.#revealResetTimeouts.get(title);
    if (existingTimeout) clearTimeout(existingTimeout);

    title.classList.remove('menu-list__link-title--revealing');
    // Force a reflow so the animation restarts even on rapid re-clicks.
    void title.offsetWidth;
    title.classList.add('menu-list__link-title--revealing');

    const charCount = title.querySelectorAll('.menu-list__link-title-char').length;
    const totalDuration = REVEAL_RESET_BUFFER + charCount * REVEAL_CHAR_STAGGER;

    const timeoutId = setTimeout(() => {
      title.classList.remove('menu-list__link-title--revealing');
      this.#revealResetTimeouts.delete(title);
    }, totalDuration);

    this.#revealResetTimeouts.set(title, timeoutId);
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}